import { useEffect, useState } from "react";
import { Wallet, ArrowDownRight, ArrowUpRight, Loader2, Clock, AlertCircle } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { fmtNaira } from "@/lib/api";

export default function AdminWallet() {
  const [data, setData] = useState({ transactions: [], totals: { total_in: 0, total_out: 0, count: 0, system_wallet_balance: 0 } });
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dir, setDir] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const q = dir ? `?direction=${dir}&limit=200` : "?limit=200";
      const [a, p] = await Promise.all([
        adminApi.get(`/admin/wallet/activity${q}`),
        adminApi.get("/admin/wallet/pending-transfers"),
      ]);
      setData(a.data);
      setPending(p.data.pending_transfers || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [dir]);

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="admin-wallet">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
          <Wallet className="w-6 h-6 text-kudi-green" /> Wallet activity
        </h1>
        <p className="text-sm text-kudi-ink/60 mt-1">All money in/out across every trader&apos;s Kobolog wallet.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard title="System balance" value={fmtNaira(data.totals.system_wallet_balance)} tone="bg-gradient-to-br from-kudi-green to-kudi-green-dark text-white" />
        <StatCard title="Total in (recent)" value={fmtNaira(data.totals.total_in)} icon={ArrowDownRight} tone="bg-emerald-50 text-emerald-800 border border-emerald-200" />
        <StatCard title="Total out (recent)" value={fmtNaira(data.totals.total_out)} icon={ArrowUpRight} tone="bg-rose-50 text-rose-800 border border-rose-200" />
        <StatCard title="Rows loaded" value={data.totals.count} tone="bg-white border border-border" />
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 mb-4">
        {[
          { k: "", l: "All" },
          { k: "in", l: "Money in" },
          { k: "out", l: "Money out" },
        ].map((c) => (
          <button
            key={c.k || "all"}
            onClick={() => setDir(c.k)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${dir === c.k ? "bg-kudi-terracotta text-white border-kudi-terracotta" : "bg-white text-kudi-ink/70 border-border hover:border-kudi-terracotta"}`}
            data-testid={`wallet-filter-${c.k || "all"}`}
          >
            {c.l}
          </button>
        ))}
      </div>

      {/* Pending transfers */}
      {pending.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6" data-testid="pending-transfers">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-amber-700" />
            <h2 className="font-semibold text-sm text-amber-900">Pending Paystack transfers ({pending.length})</h2>
          </div>
          <ul className="text-xs space-y-1.5">
            {pending.slice(0, 8).map((p) => (
              <li key={p.id || p.transfer_code} className="flex items-center justify-between gap-2 py-1 border-t border-amber-200/60 first:border-t-0">
                <div className="min-w-0">
                  <span className="font-mono text-amber-900">{p.reference}</span>
                  <span className="text-amber-800/70"> · {p.recipient_name || p.recipient_account} · {p.status}</span>
                </div>
                <span className="font-bold text-amber-900 shrink-0">{fmtNaira(p.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Activity table */}
      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-kudi-ink/40 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading transactions…
          </div>
        ) : data.transactions.length === 0 ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-10 h-10 mx-auto text-kudi-ink/30 mb-2" />
            <div className="text-kudi-ink/50">No wallet activity yet.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-kudi-cream text-xs text-kudi-ink/60 uppercase">
              <tr>
                <th className="text-left px-3 py-2 font-medium">When</th>
                <th className="text-left px-3 py-2 font-medium">Trader</th>
                <th className="text-left px-3 py-2 font-medium">Source</th>
                <th className="text-right px-3 py-2 font-medium">Amount</th>
                <th className="text-left px-3 py-2 font-medium hidden md:table-cell">Reference</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.transactions.map((t) => (
                <tr key={t.id || t.reference} className="hover:bg-accent/30 transition" data-testid={`wallet-row-${t.id || t.reference}`}>
                  <td className="px-3 py-2 text-xs text-kudi-ink/60 whitespace-nowrap">
                    {new Date(t.at || t.created_at).toLocaleString("en-NG", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-3 py-2 min-w-0">
                    <div className="font-medium truncate max-w-[180px]">{t.user?.business_name || t.user?.owner_name || t.user?.email || "—"}</div>
                    <div className="text-[11px] text-kudi-ink/50 truncate max-w-[180px]">{t.user?.email}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className="inline-block px-2 py-0.5 rounded bg-kudi-cream text-kudi-ink/70 font-mono">{t.source || "—"}</span>
                  </td>
                  <td className={`px-3 py-2 text-right font-bold tabular-nums ${t.direction === "in" ? "text-emerald-700" : "text-rose-700"}`}>
                    {t.direction === "in" ? "+" : "−"} {fmtNaira(t.amount)}
                  </td>
                  <td className="px-3 py-2 text-xs text-kudi-ink/50 font-mono truncate max-w-[220px] hidden md:table-cell" title={t.reference}>{t.reference || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, tone }) {
  return (
    <div className={`rounded-2xl p-4 ${tone}`}>
      <div className="text-xs opacity-80 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </div>
      <div className="text-2xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}
